import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import InteractiveBackground from './components/InteractiveBackground';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import Skills from './pages/Skills';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import usePageTracking from './hooks/usePageTracking';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2997FF',
      light: '#64b5f6',
      dark: '#0071e3',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#86868b',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#000000',
      paper: 'rgba(255,255,255,0.05)',
    },
    text: {
      primary: '#F5F5F7',
      secondary: 'rgba(245,245,247,0.56)',
    },
  },
  typography: {
    fontFamily: '"Mona Sans", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 'clamp(48px, 9vw, 88px)',
      fontWeight: 700,
      letterSpacing: '-0.045em',
      lineHeight: 1.02,
      color: '#F5F5F7',
    },
    h2: {
      fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 'clamp(30px, 5.5vw, 52px)',
      fontWeight: 600,
      letterSpacing: '-0.035em',
      lineHeight: 1.08,
      color: '#F5F5F7',
    },
    h3: {
      fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 'clamp(22px, 3.5vw, 32px)',
      fontWeight: 600,
      letterSpacing: '-0.025em',
      color: '#F5F5F7',
    },
    body1: {
      fontSize: '17px',
      lineHeight: 1.8,
      letterSpacing: '0.002em',
      color: 'rgba(245,245,247,0.56)',
    },
    body2: {
      fontSize: '15px',
      lineHeight: 1.6,
      color: 'rgba(245,245,247,0.56)',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '15px',
          padding: '10px 22px',
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          background: '#2997FF',
          color: '#fff',
          '&:hover': {
            background: '#1a84e8',
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.2)',
          color: '#F5F5F7',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.4)',
            background: 'rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          boxShadow: '0 2px 24px rgba(0,0,0,0.6)',
          transition: 'all 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 2px 24px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.08)',
          borderRadius: 4,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
  },
});

function AppContent() {
  usePageTracking();
  return (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      <InteractiveBackground />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Navbar />
        <Routes>
          <Route path="/"         element={<Home />} />
          <Route path="/skills"   element={<Skills />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/contact"  element={<Contact />} />
          <Route path="/blog"     element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPost />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router basename={process.env.PUBLIC_URL}>
          <AppContent />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}
